/* REACT */
import { useEffect, useRef, useState } from "react";

/* PLUGINS */
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, X } from "lucide-react";

/* COMPONENTS */
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

/* SCHEMAS */
import { feedbackSchema, type FeedbackFormData } from "../schemas";

/* HOOKS */
import { useSubmitFeedback, useFeedbackCategories } from "../hooks/useFeedback";

/* CONSTANTS */
import { FEEDBACK_FIELD_LIMITS, FEEDBACK_IMAGE_LIMITS } from "../constants";

interface FeedbackDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * FeedbackDialog
 * Modal form for submitting feedback with optional image attachments.
 * Categories are fetched from the server and the current page URL is captured automatically.
 * Last Updated Date: August 13, 2026
 * @author Jette
 */
export const FeedbackDialog = ({ open, onClose }: FeedbackDialogProps) => {
    /* Only fetch categories while the dialog is open. */
    const { data: categories = [], isLoading: isLoadingCategories } =
        useFeedbackCategories(open);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FeedbackFormData>({
        resolver: zodResolver(feedbackSchema),
        defaultValues: {
            category_id: 0,
            description: "",
        },
    });

    const [selected_images, set_selected_images] = useState<File[]>([]);
    const [image_error, set_image_error] = useState<string>("");
    const [preview_urls, set_preview_urls] = useState<string[]>([]);
    const file_input_ref = useRef<HTMLInputElement>(null);

    /* Revoke object URLs when the list changes or the component unmounts to prevent memory leaks. */
    useEffect(() => {
        return () => {
            preview_urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [preview_urls]);

    const clearImages = () => {
        preview_urls.forEach(url => URL.revokeObjectURL(url));
        set_selected_images([]);
        set_preview_urls([]);
        set_image_error("");
        /* Reset the hidden input so the same file can be re-selected after removal. */
        if (file_input_ref.current) file_input_ref.current.value = "";
    };

    const { mutate: submitFeedback, isPending } = useSubmitFeedback({
        onSuccess: () => {
            reset();
            clearImages();
            onClose();
        },
    });

    /* Reset the form each time the dialog opens so stale input never lingers. */
    useEffect(() => {
        if (open) {
            reset({ category_id: 0, description: "" });
            clearImages();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reset]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(event.target.files ?? []);
        if (picked.length === 0) return;

        /* Reset the input value so the same file can be re-added after removal. */
        if (file_input_ref.current) file_input_ref.current.value = "";

        set_image_error("");

        const remaining_slots = FEEDBACK_IMAGE_LIMITS.maxCount - selected_images.length;
        if (picked.length > remaining_slots) {
            set_image_error(`You can attach up to ${FEEDBACK_IMAGE_LIMITS.maxCount} images.`);
            return;
        }

        for (const file of picked) {
            const is_allowed_type = (FEEDBACK_IMAGE_LIMITS.allowedTypes as readonly string[]).includes(file.type);
            if (!is_allowed_type) {
                set_image_error("Only JPEG, PNG, and WebP images are allowed.");
                return;
            }
            if (file.size > FEEDBACK_IMAGE_LIMITS.maxSizeBytes) {
                set_image_error(`Each image must be ${FEEDBACK_IMAGE_LIMITS.maxSizeMB}MB or smaller.`);
                return;
            }
        }

        const new_urls = picked.map(file => URL.createObjectURL(file));
        set_selected_images(prev => [...prev, ...picked]);
        set_preview_urls(prev => [...prev, ...new_urls]);
    };

    const handleRemoveImage = (index: number) => {
        URL.revokeObjectURL(preview_urls[index]);
        set_selected_images(prev => prev.filter((_, i) => i !== index));
        set_preview_urls(prev => prev.filter((_, i) => i !== index));
        set_image_error("");
    };

    const handleFormSubmit = (data: FeedbackFormData) => {
        /* Capture the page the user was on so testers can reproduce the context. */
        const page_url = typeof window !== "undefined" ? window.location.pathname : null;
        submitFeedback({ ...data, page_url, images: selected_images });
    };

    const handleClose = () => {
        reset();
        clearImages();
        onClose();
    };

    const is_add_disabled = selected_images.length >= FEEDBACK_IMAGE_LIMITS.maxCount;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent align="center" className="w-full sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-foreground">Send Feedback</DialogTitle>
                    <DialogDescription>
                        Report a bug, share a suggestion, or ask a question. Your feedback goes straight to the team.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="feedback-category">
                            Category <span className="text-destructive">*</span>
                        </Label>
                        <Controller
                            control={control}
                            name="category_id"
                            render={({ field }) => (
                                <Select
                                    value={field.value ? String(field.value) : ""}
                                    onValueChange={(value) => field.onChange(Number(value))}
                                    disabled={isLoadingCategories}
                                >
                                    <SelectTrigger id="feedback-category" className="w-full">
                                        <SelectValue
                                            placeholder={isLoadingCategories ? "Loading categories..." : "Select a category"}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((category) => (
                                            <SelectItem key={category.id} value={String(category.id)}>
                                                {category.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        {errors.category_id && (
                            <p className="text-sm text-destructive">{errors.category_id.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="feedback-description">
                            Description <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                            id="feedback-description"
                            placeholder="Tell us what's on your mind..."
                            rows={5}
                            maxLength={FEEDBACK_FIELD_LIMITS.descriptionMax}
                            {...register("description")}
                            className={`focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand border border-border ${errors.description ? "border-destructive" : ""}`}
                        />
                        {errors.description && (
                            <p className="text-sm text-destructive">{errors.description.message}</p>
                        )}
                    </div>

                    {/* Image upload — hidden input triggered by the styled button below */}
                    <div className="space-y-2">
                        <input
                            ref={file_input_ref}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={handleFileSelect}
                            className="hidden"
                            aria-hidden="true"
                        />

                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => file_input_ref.current?.click()}
                            disabled={is_add_disabled}
                            className="w-full touch-manipulation"
                        >
                            📎 Add image ({selected_images.length} / {FEEDBACK_IMAGE_LIMITS.maxCount})
                        </Button>

                        {selected_images.length > 0 && (
                            <div className="flex flex-row gap-2 flex-wrap">
                                {preview_urls.map((url, index) => (
                                    <div key={url} className="relative w-20 h-20 shrink-0">
                                        <img
                                            src={url}
                                            alt={`Attachment ${index + 1}`}
                                            className="w-20 h-20 object-cover rounded-md border border-border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImage(index)}
                                            aria-label={`Remove image ${index + 1}`}
                                            className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-destructive text-destructive-foreground shadow-sm touch-manipulation"
                                        >
                                            <X className="w-3 h-3" aria-hidden="true" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {image_error && (
                            <p className="text-sm text-destructive" role="alert">
                                {image_error}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="flex-row gap-3 w-full">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isPending}
                            className="text-subtle-foreground px-6 flex-1"
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending} className="px-6 flex-1">
                            {isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                "Send Feedback"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
